import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
    Aangifte,
    BokkzIbDataService,
    IncomeTaxEntity,
    JaarrekeningBalans,
    Onderneming,
    Ondernemingsvorm,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { filter, Observable, shareReplay, tap } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SpecGebouwenEigenComponent } from '../spec-gebouwen-eigen/spec-gebouwen-eigen.component';
import { SpecMachinesInstallatiesComponent } from '../spec-machines-installaties/spec-machines-installaties.component';
import { SpecOverigMaterieleVasteActivaComponent } from '../spec-overig-materiele-vaste-activa/spec-overig-materiele-vaste-activa.component';
import { SpecOmzetbelastingActivaComponent } from '../spec-omzetbelasting-activa/spec-omzetbelasting-activa.component';

type BalansGroup = FormGroup<{
    omschrijving: FormControl<string | null | undefined>;
    waardeOpening: FormControl<number | null | undefined>;
    waardeSlot: FormControl<number | null | undefined>;
    aanschafWaarde: FormControl<number | null | undefined>;
    restWaarde: FormControl<number | null | undefined>;
    bodemwaarde: FormControl<number | null | undefined>;
    nominaleWaarde: FormControl<number | null | undefined>;
    gebruikBinnenEigenOnderneming: FormControl<boolean | null | undefined>;
    omzetbelastingDitBoekjaar: FormControl<number | null | undefined>;
    omzetbelastingVorigBoekjaar: FormControl<number | null | undefined>;
    omzetbelastingOudereBoekjaren: FormControl<number | null | undefined>;
}>;

type TotalGroup = FormGroup<{
    totaalOpening: FormControl<number | null>;
    totaalSlot: FormControl<number | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-balans-activa',
    templateUrl: './balans-activa.component.html',
    styleUrls: ['./balans-activa.component.scss'],
    imports: [
        SharedUiFormControlsModule,
        SharedUiDetailsModule,
        CommonModule,
        SpecGebouwenEigenComponent,
        SpecMachinesInstallatiesComponent,
        SpecOverigMaterieleVasteActivaComponent,
        SpecOmzetbelastingActivaComponent,
    ],
})
export class BalansActivaComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    public dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    public indexOnderneming = 0;
    public heeftOndernemingen = false;
    public heeftSamenwerkingsverband = false;
    public labelBalansOpening = '';
    public labelBalansSlot = '';
    public isPartner = false;

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly indexOnderneming$: Observable<number> = this.dataService.indexOnderneming$.pipe(
        tap((data) => {
            this.indexOnderneming = data;
        }),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public immaterieleVasteActivaGoodwllFg: BalansGroup = this.createJaarrekeningGroup();
    public immaterieleVasteActivaOverigFg: BalansGroup = this.createJaarrekeningGroup();
    public materieleVasteActivaGebouwenEigenFg: BalansGroup = this.createJaarrekeningGroup();
    public materieleVasteActivaMachinesFg: BalansGroup = this.createJaarrekeningGroup();
    public materieleVasteActivaOverigFg: BalansGroup = this.createJaarrekeningGroup();
    public financieleVasteActivaFg: BalansGroup = this.createJaarrekeningGroup();
    public vlottendeActivaVoorraadFg: BalansGroup = this.createJaarrekeningGroup();
    public vlottendeActivaVoorraadOhwFg: BalansGroup = this.createJaarrekeningGroup();
    public vlottendeActivaVorderingenDebiteurenFg: BalansGroup = this.createJaarrekeningGroup();
    public vlottendeActivaVorderingenOmzetbelastingFg: BalansGroup = this.createJaarrekeningGroup();
    public vlottendeActivaVorderingenOverigFg: BalansGroup = this.createJaarrekeningGroup();
    public effectenFg: BalansGroup = this.createJaarrekeningGroup();
    public liquideMiddelenFg: BalansGroup = this.createJaarrekeningGroup();

    public totalFg: TotalGroup = new FormGroup({
        totaalOpening: new FormControl<number | null>(null),
        totaalSlot: new FormControl<number | null>(null),
    });

    private createJaarrekeningGroup(): BalansGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | undefined>(undefined),
            waardeOpening: new FormControl<number | undefined>(undefined),
            waardeSlot: new FormControl<number | undefined>(undefined),
            aanschafWaarde: new FormControl<number | undefined>(undefined),
            restWaarde: new FormControl<number | undefined>(undefined),
            bodemwaarde: new FormControl<number | undefined>(undefined),
            nominaleWaarde: new FormControl<number | undefined>(undefined),
            gebruikBinnenEigenOnderneming: new FormControl<boolean | undefined>(undefined),
            omzetbelastingDitBoekjaar: new FormControl<number | undefined>(undefined),
            omzetbelastingVorigBoekjaar: new FormControl<number | undefined>(undefined),
            omzetbelastingOudereBoekjaren: new FormControl<number | undefined>(undefined),
        });
    }

    ngOnInit(): void {
        this.indexOnderneming$.subscribe();
        this.dataService.partnerAangifte$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((partner) => {
            this.isPartner = partner;
            this.incomeTaxEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((entity) => {
                if (partner) {
                    this.handleSwitch(entity, true);
                } else {
                    this.handleSwitch(entity, false);
                }
            });
        });
        [
            this.immaterieleVasteActivaGoodwllFg,
            this.immaterieleVasteActivaOverigFg,
            this.materieleVasteActivaGebouwenEigenFg,
            this.materieleVasteActivaMachinesFg,
            this.materieleVasteActivaOverigFg,
            this.financieleVasteActivaFg,
            this.vlottendeActivaVoorraadFg,
            this.vlottendeActivaVoorraadOhwFg,
            this.vlottendeActivaVorderingenDebiteurenFg,
            this.vlottendeActivaVorderingenOmzetbelastingFg,
            this.vlottendeActivaVorderingenOverigFg,
            this.effectenFg,
            this.liquideMiddelenFg,
        ].forEach((fg) => {
            const waardeOpeningControl = fg.controls.waardeOpening;
            waardeOpeningControl?.valueChanges.subscribe((val) => {
                if (val !== null) {
                    this.saveForm();
                }
            });
            const waardeSlotControl = fg.controls.waardeSlot;
            waardeSlotControl?.valueChanges.subscribe((val) => {
                if (val !== null) {
                    this.saveForm();
                }
            });
        });
    }

    private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
        const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
        if (details) {
            this.resetAndPatchForms(details);
            this.getLabels(details);
        }
    }

    private resetAndPatchForms(details: any): void {
        this.resetFormGroups();
        this.heeftOndernemingen = false;
        this.heeftSamenwerkingsverband = false;
        const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
        this.patchFormGroups(ondernemingen);
    }

    private resetFormGroups() {
        this.immaterieleVasteActivaGoodwllFg.reset({ omschrijving: 'goodwill' }, { emitEvent: false });
        this.immaterieleVasteActivaOverigFg.reset({ omschrijving: 'immaterieleVasteActiva' }, { emitEvent: false });
        this.materieleVasteActivaGebouwenEigenFg.reset({ omschrijving: 'bedrijfsGebouwen' }, { emitEvent: false });
        this.materieleVasteActivaMachinesFg.reset({ omschrijving: 'machinesEnInstallaties' }, { emitEvent: false });
        this.materieleVasteActivaOverigFg.reset({ omschrijving: 'andereVasteActiva' }, { emitEvent: false });
        this.financieleVasteActivaFg.reset({ omschrijving: 'financieleVasteActiva' }, { emitEvent: false });
        this.vlottendeActivaVoorraadFg.reset({ omschrijving: 'voorraad' }, { emitEvent: false });
        this.vlottendeActivaVoorraadOhwFg.reset({ omschrijving: 'onderhandenWerk' }, { emitEvent: false });
        this.vlottendeActivaVorderingenDebiteurenFg.reset({ omschrijving: 'debiteuren' }, { emitEvent: false });
        this.vlottendeActivaVorderingenOmzetbelastingFg.reset({ omschrijving: 'omzetbelastingVordering' }, { emitEvent: false });
        this.vlottendeActivaVorderingenOverigFg.reset({ omschrijving: 'overigeVorderingen' }, { emitEvent: false });
        this.effectenFg.reset({ omschrijving: 'effecten' }, { emitEvent: false });
        this.liquideMiddelenFg.reset({ omschrijving: 'liquideMiddelen' }, { emitEvent: false });
        this.totalFg.reset({}, { emitEvent: false });
    }

    private patchFormGroups(ondernemingen: Onderneming[]): void {
        // Reset all groups to clear previous values
        this.resetFormGroups();
        const onderneming = ondernemingen[this.indexOnderneming];
        if (!onderneming) return;
        this.heeftOndernemingen = true;
        this.heeftSamenwerkingsverband = onderneming.ondernemingsvorm !== Ondernemingsvorm.EENMANSZAAK;

        // immateriele vaste activa
        const goodWill =
            onderneming.immaterieleVasteActiva?.find((item: any) => item.omschrijving === 'goodwill') ??
            this.createJaarrekeningBalans('goodwill');
        const immaterieleVasteActiva =
            onderneming.immaterieleVasteActiva?.find((item: any) => item.omschrijving === 'immaterieleVasteActiva') ??
            this.createJaarrekeningBalans('immaterieleVasteActiva');
        this.immaterieleVasteActivaGoodwllFg.patchValue(goodWill, { emitEvent: false });
        this.immaterieleVasteActivaOverigFg.patchValue(immaterieleVasteActiva, { emitEvent: false });

        // materiele vaste activa
        const bedrijfsGebouwen =
            onderneming.materieleVasteActiva?.find((item: any) => item.omschrijving === 'bedrijfsGebouwen') ??
            this.createJaarrekeningBalans('bedrijfsGebouwen');
        const machinesEnInstallaties =
            onderneming.materieleVasteActiva?.find((item: any) => item.omschrijving === 'machinesEnInstallaties') ??
            this.createJaarrekeningBalans('machinesEnInstallaties');
        const andereVasteActiva =
            onderneming.materieleVasteActiva?.find((item: any) => item.omschrijving === 'andereVasteActiva') ??
            this.createJaarrekeningBalans('andereVasteActiva');
        this.materieleVasteActivaGebouwenEigenFg.patchValue(bedrijfsGebouwen, { emitEvent: false });
        this.materieleVasteActivaMachinesFg.patchValue(machinesEnInstallaties, { emitEvent: false });
        this.materieleVasteActivaOverigFg.patchValue(andereVasteActiva, { emitEvent: false });

        // financiele vaste activa
        const financieleVasteActiva =
            onderneming.financieleVasteActiva?.find((item: any) => item.omschrijving === 'financieleVasteActiva') ??
            this.createJaarrekeningBalans('financieleVasteActiva');
        this.financieleVasteActivaFg.patchValue(financieleVasteActiva, { emitEvent: false });

        // vlottende activa - voorraad
        const voorraad =
            onderneming.vlottendeActivaVoorraad?.find((item: any) => item.omschrijving === 'voorraad') ??
            this.createJaarrekeningBalans('voorraad');
        const onderhandenWerk =
            onderneming.vlottendeActivaVoorraad?.find((item: any) => item.omschrijving === 'onderhandenWerk') ??
            this.createJaarrekeningBalans('onderhandenWerk');
        this.vlottendeActivaVoorraadFg.patchValue(voorraad, { emitEvent: false });
        this.vlottendeActivaVoorraadOhwFg.patchValue(onderhandenWerk, { emitEvent: false });

        // vlottende activa - vorderingen
        const debiteuren =
            onderneming.vlottendeActivaVorderingen?.find((item: any) => item.omschrijving === 'debiteuren') ??
            this.createJaarrekeningBalans('debiteuren');
        const omzetbelastingVordering =
            onderneming.vlottendeActivaVorderingen?.find((item: any) => item.omschrijving === 'omzetbelastingVordering') ??
            this.createJaarrekeningBalans('omzetbelastingVordering');
        const overigeVorderingen =
            onderneming.vlottendeActivaVorderingen?.find((item: any) => item.omschrijving === 'overigeVorderingen') ??
            this.createJaarrekeningBalans('overigeVorderingen');
        this.vlottendeActivaVorderingenDebiteurenFg.patchValue(debiteuren, { emitEvent: false });
        this.vlottendeActivaVorderingenOmzetbelastingFg.patchValue(omzetbelastingVordering, { emitEvent: false });
        this.vlottendeActivaVorderingenOverigFg.patchValue(overigeVorderingen, { emitEvent: false });

        // liquide middelen
        const effecten =
            onderneming.effecten?.find((item: any) => item.omschrijving === 'effecten') ?? this.createJaarrekeningBalans('effecten');
        const liquideMiddelen =
            onderneming.liquideMiddelen?.find((item: any) => item.omschrijving === 'liquideMiddelen') ??
            this.createJaarrekeningBalans('liquideMiddelen');
        this.effectenFg.patchValue(effecten, { emitEvent: false });
        this.liquideMiddelenFg.patchValue(liquideMiddelen, { emitEvent: false });
    }

    getLabels(data: Aangifte) {
        let begindatum;
        if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.startOnderneming != null) {
            begindatum = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].startOnderneming!;
        } else {
            begindatum = new Date(data.aangifteJaar, 0, 1);
        }
        let slotdatum;
        if (data.box1?.winstUitOnderneming?.ondernemingen.at(this.indexOnderneming)?.datumStaking != null) {
            slotdatum = data.box1.winstUitOnderneming.ondernemingen[this.indexOnderneming].datumStaking!;
        } else {
            slotdatum = new Date(data.aangifteJaar, 11, 31);
        }
        this.labelBalansOpening =
            begindatum != null
                ? `Balans ${new Date(begindatum).toLocaleDateString('nl-NL', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                  })}`
                : 'Openingsbalans';
        this.labelBalansSlot =
            slotdatum != null
                ? `Balans ${new Date(slotdatum).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
                : 'Slotbalans';
    }

    ngOnDestroy(): void {
        this.dataService.calculateLatestIncomeTaxReturn();
    }

    saveForm(): void {
        const idx = this.indexOnderneming;

        const immaterieleVasteActivaUpdates = [
            this.immaterieleVasteActivaGoodwllFg.getRawValue(),
            this.immaterieleVasteActivaOverigFg.getRawValue(),
        ];
        const materieleVasteActivaUpdates = [
            this.materieleVasteActivaGebouwenEigenFg.getRawValue(),
            this.materieleVasteActivaMachinesFg.getRawValue(),
            this.materieleVasteActivaOverigFg.getRawValue(),
        ];
        const financieleVasteActivaUpdates = [this.financieleVasteActivaFg.getRawValue()];
        const vlottendeActivaVoorraadUpdates = [
            this.vlottendeActivaVoorraadFg.getRawValue(),
            this.vlottendeActivaVoorraadOhwFg.getRawValue(),
        ];
        const vlottendeActivaVorderingenUpdates = [
            this.vlottendeActivaVorderingenDebiteurenFg.getRawValue(),
            this.vlottendeActivaVorderingenOmzetbelastingFg.getRawValue(),
            this.vlottendeActivaVorderingenOverigFg.getRawValue(),
        ];
        const effectenUpdates = [this.effectenFg.getRawValue()];
        const liquideMiddelenUpdates = [this.liquideMiddelenFg.getRawValue()];

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }
            const update = (aangifte: any) => {
                const existingOndernemingen = (aangifte.box1?.winstUitOnderneming?.ondernemingen as Onderneming[]) || [];
                const onderneming = existingOndernemingen[idx] || {};

                const updatedOnderneming: Partial<Onderneming> = {
                    ...onderneming,
                    immaterieleVasteActiva: immaterieleVasteActivaUpdates,
                    materieleVasteActiva: materieleVasteActivaUpdates,
                    financieleVasteActiva: financieleVasteActivaUpdates,
                    vlottendeActivaVoorraad: vlottendeActivaVoorraadUpdates,
                    vlottendeActivaVorderingen: vlottendeActivaVorderingenUpdates,
                    effecten: effectenUpdates,
                    liquideMiddelen: liquideMiddelenUpdates,
                };
                // We bereken EV hier:
                if (!updatedOnderneming.eigenVermogen) {
                    updatedOnderneming.eigenVermogen = [];
                }

                let eigenVermogen = updatedOnderneming.eigenVermogen.find((item) => item.omschrijving === 'eigenVermogen');

                if (!eigenVermogen) {
                    eigenVermogen = {
                        omschrijving: 'eigenVermogen',
                    } as JaarrekeningBalans;

                    updatedOnderneming.eigenVermogen.push(eigenVermogen);
                }

                eigenVermogen.waardeSlot = this.berekenEigenVermogenSlot(updatedOnderneming);
                eigenVermogen.waardeOpening = this.berekenEigenVermogenOpening(updatedOnderneming);


                // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
                const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
                const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => (i === idx ? cleanedOnderneming : o));

                return {
                    ...aangifte,
                    box1: {
                        ...aangifte.box1,
                        winstUitOnderneming: {
                            ...aangifte.box1?.winstUitOnderneming,
                            ondernemingen: updatedOndernemingen,
                        },
                    },
                };
            };
            const updatedAangifte = this.isPartner
                ? {
                      ...currentEntity.aangifte,
                      partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
                  }
                : update(currentEntity.aangifte);
            return this.dataService.deepClean({
                ...currentEntity,
                aangifte: updatedAangifte,
            }) as IncomeTaxEntity;
        });
    }

    private createJaarrekeningBalans(omschrijving: string): JaarrekeningBalans {
        return new FormGroup({
            waardeOpening: new FormControl<number | null>(null),
            waardeSlot: new FormControl<number | null>(null),
            omschrijving: new FormControl<string | null>(omschrijving),
            nominaleWaarde: new FormControl<number | null>(null),
            aanschafWaarde: new FormControl<number | null>(null),
            restWaarde: new FormControl<number | null>(null),
            bodemwaarde: new FormControl<number | null>(null),
            omzetbelastingDitBoekjaar: new FormControl<number | null>(null),
            omzetbelastingVorigBoekjaar: new FormControl<number | null>(null),
            omzetbelastingOudereBoekjaren: new FormControl<number | null>(null),
            gebruikBinnenEigenOnderneming: new FormControl<number | null>(null),
        });
    }

    public getTotaalActivaOpening(): any {
        const goodWill = this.immaterieleVasteActivaGoodwllFg?.get('waardeOpening')?.value ?? 0;
        const immaterieleVasteActiva = this.immaterieleVasteActivaOverigFg?.get('waardeOpening')?.value ?? 0;
        const bedrijfsGebouwen = this.materieleVasteActivaGebouwenEigenFg?.get('waardeOpening')?.value ?? 0;
        const machinesEnInstallaties = this.materieleVasteActivaMachinesFg?.get('waardeOpening')?.value ?? 0;
        const andereVasteActiva = this.materieleVasteActivaOverigFg?.get('waardeOpening')?.value ?? 0;
        const financieleVasteActiva = this.financieleVasteActivaFg?.get('waardeOpening')?.value ?? 0;
        const onderhandenWerk = this.vlottendeActivaVoorraadOhwFg?.get('waardeOpening')?.value ?? 0;
        const voorraad = this.vlottendeActivaVoorraadFg?.get('waardeOpening')?.value ?? 0;
        const debiteuren = this.vlottendeActivaVorderingenDebiteurenFg?.get('waardeOpening')?.value ?? 0;
        const omzetbelastingVordering = this.vlottendeActivaVorderingenOmzetbelastingFg?.get('waardeOpening')?.value ?? 0;
        const overigeVorderingen = this.vlottendeActivaVorderingenOverigFg?.get('waardeOpening')?.value ?? 0;
        const effecten = this.effectenFg?.get('waardeOpening')?.value ?? 0;
        const liquideMiddelen = this.liquideMiddelenFg?.get('waardeOpening')?.value ?? 0;
        const sum =
            goodWill +
            immaterieleVasteActiva +
            bedrijfsGebouwen +
            machinesEnInstallaties +
            andereVasteActiva +
            onderhandenWerk +
            voorraad +
            financieleVasteActiva +
            debiteuren +
            omzetbelastingVordering +
            overigeVorderingen +
            effecten +
            liquideMiddelen;
        this.totalFg.patchValue({ totaalOpening: sum }, { emitEvent: false });
        return sum;
    }

    public getTotaalActivaSlot(): any {
        const goodWill = this.immaterieleVasteActivaGoodwllFg?.get('waardeSlot')?.value ?? 0;
        const immaterieleVasteActiva = this.immaterieleVasteActivaOverigFg?.get('waardeSlot')?.value ?? 0;
        const bedrijfsGebouwen = this.materieleVasteActivaGebouwenEigenFg?.get('waardeSlot')?.value ?? 0;
        const machinesEnInstallaties = this.materieleVasteActivaMachinesFg?.get('waardeSlot')?.value ?? 0;
        const andereVasteActiva = this.materieleVasteActivaOverigFg?.get('waardeSlot')?.value ?? 0;
        const financieleVasteActiva = this.financieleVasteActivaFg?.get('waardeSlot')?.value ?? 0;
        const onderhandenWerk = this.vlottendeActivaVoorraadOhwFg?.get('waardeSlot')?.value ?? 0;
        const voorraad = this.vlottendeActivaVoorraadFg?.get('waardeSlot')?.value ?? 0;
        const debiteuren = this.vlottendeActivaVorderingenDebiteurenFg?.get('waardeSlot')?.value ?? 0;
        const omzetbelastingVordering = this.vlottendeActivaVorderingenOmzetbelastingFg?.get('waardeSlot')?.value ?? 0;
        const overigeVorderingen = this.vlottendeActivaVorderingenOverigFg?.get('waardeSlot')?.value ?? 0;
        const effecten = this.effectenFg?.get('waardeSlot')?.value ?? 0;
        const liquideMiddelen = this.liquideMiddelenFg?.get('waardeSlot')?.value ?? 0;
        const sum =
            goodWill +
            immaterieleVasteActiva +
            bedrijfsGebouwen +
            machinesEnInstallaties +
            andereVasteActiva +
            onderhandenWerk +
            voorraad +
            financieleVasteActiva +
            debiteuren +
            omzetbelastingVordering +
            overigeVorderingen +
            effecten +
            liquideMiddelen;
        this.totalFg.patchValue({ totaalSlot: sum }, { emitEvent: false });
        return sum;
    }

    berekenEigenVermogenSlot(onderneming: Partial<Onderneming>): any {
        let totaalActiva = 0;
        let totaalPassivaZonderEv = 0;
        //immateriele vaste activa
        const goodWill =
            onderneming?.immaterieleVasteActiva?.filter((item) => item !== null).find((item) => item.omschrijving === 'goodwill')
                ?.waardeSlot || 0;
        const immaterieleVasteActiva =
            onderneming?.immaterieleVasteActiva
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'immaterieleVasteActiva')?.waardeSlot || 0;
        const bedrijfsGebouwen =
            onderneming?.materieleVasteActiva?.filter((item) => item !== null).find((item) => item.omschrijving === 'bedrijfsGebouwen')
                ?.waardeSlot || 0;
        const machinesEnInstallaties =
            onderneming?.materieleVasteActiva
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'machinesEnInstallaties')?.waardeSlot || 0;
        const andereVasteActiva =
            onderneming?.materieleVasteActiva?.filter((item) => item !== null).find((item) => item.omschrijving === 'andereVasteActiva')
                ?.waardeSlot || 0;
        const financieleVasteActiva =
            onderneming?.financieleVasteActiva
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'financieleVasteActiva')?.waardeSlot || 0;
        const onderhandenWerk =
            onderneming?.vlottendeActivaVoorraad?.filter((item) => item !== null).find((item) => item.omschrijving === 'onderhandenWerk')
                ?.waardeSlot || 0;
        const voorraad =
            onderneming?.vlottendeActivaVoorraad?.filter((item) => item !== null).find((item) => item.omschrijving === 'voorraad')
                ?.waardeSlot || 0;
        const debiteuren =
            onderneming?.vlottendeActivaVorderingen?.filter((item) => item !== null).find((item) => item.omschrijving === 'debiteuren')
                ?.waardeSlot || 0;
        const omzetbelastingVordering =
            onderneming?.vlottendeActivaVorderingen
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'omzetbelastingVordering')?.waardeSlot || 0;
        const overigeVorderingen =
            onderneming?.vlottendeActivaVorderingen
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'overigeVorderingen')?.waardeSlot || 0;
        const effecten =
            onderneming?.effecten?.filter((item) => item !== null).find((item) => item.omschrijving === 'effecten')?.waardeSlot || 0;
        const liquideMiddelen =
            onderneming?.liquideMiddelen?.filter((item) => item !== null).find((item) => item.omschrijving === 'liquideMiddelen')
                ?.waardeSlot || 0;

        totaalActiva =
            goodWill +
            immaterieleVasteActiva +
            bedrijfsGebouwen +
            machinesEnInstallaties +
            andereVasteActiva +
            financieleVasteActiva +
            voorraad +
            onderhandenWerk +
            debiteuren +
            omzetbelastingVordering +
            overigeVorderingen +
            effecten +
            liquideMiddelen;
        const egalisatieReserve =
            onderneming?.eigenVermogen?.filter((item) => item !== null).find((item) => item.omschrijving === 'egalisatieReserve')
                ?.waardeSlot || 0;
        const oudedagsreserve =
            onderneming?.eigenVermogen?.filter((item) => item !== null).find((item) => item.omschrijving === 'oudedagsReserve')
                ?.waardeSlot || 0;
        const herinvesteringsreserve =
            onderneming?.eigenVermogen?.filter((item) => item !== null).find((item) => item.omschrijving === 'herinvesteringsReserve')
                ?.waardeSlot || 0;
        const garantieVoorziening =
            onderneming?.voorzieningen?.filter((item) => item !== null).find((item) => item.omschrijving === 'garantieVoorziening')
                ?.waardeSlot || 0;
        const overigeVoorzieningen =
            onderneming?.voorzieningen?.filter((item) => item !== null).find((item) => item.omschrijving === 'overigeVoorzieningen')
                ?.waardeSlot || 0;
        const kredietinstellingen =
            onderneming?.langlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'kredietinstellingen')
                ?.waardeSlot || 0;
        const langlopendeSchulden =
            onderneming?.langlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'langlopendeSchulden')
                ?.waardeSlot || 0;
        const crediteuren =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'crediteuren')
                ?.waardeSlot || 0;
        const omzetbelastingSchuld =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'omzetbelastingSchuld')
                ?.waardeSlot || 0;
        const loonheffingSchuld =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'loonheffingSchuld')
                ?.waardeSlot || 0;
        const kortlopendeSchulden =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'kortlopendeSchulden')
                ?.waardeSlot || 0;

        totaalPassivaZonderEv =
            egalisatieReserve +
            oudedagsreserve +
            herinvesteringsreserve +
            garantieVoorziening +
            overigeVoorzieningen +
            kredietinstellingen +
            langlopendeSchulden +
            crediteuren +
            loonheffingSchuld +
            omzetbelastingSchuld +
            kortlopendeSchulden;
        return totaalActiva - totaalPassivaZonderEv;
    }

    berekenEigenVermogenOpening(onderneming: Partial<Onderneming>): any {
        let totaalActiva = 0;
        let totaalPassivaZonderEv = 0;
        //immateriele vaste activa
        const goodWill =
            onderneming?.immaterieleVasteActiva?.filter((item) => item !== null).find((item) => item.omschrijving === 'goodwill')
                ?.waardeOpening || 0;
        const immaterieleVasteActiva =
            onderneming?.immaterieleVasteActiva
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'immaterieleVasteActiva')?.waardeOpening || 0;
        const bedrijfsGebouwen =
            onderneming?.materieleVasteActiva?.filter((item) => item !== null).find((item) => item.omschrijving === 'bedrijfsGebouwen')
                ?.waardeOpening || 0;
        const machinesEnInstallaties =
            onderneming?.materieleVasteActiva
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'machinesEnInstallaties')?.waardeOpening || 0;
        const andereVasteActiva =
            onderneming?.materieleVasteActiva?.filter((item) => item !== null).find((item) => item.omschrijving === 'andereVasteActiva')
                ?.waardeOpening || 0;
        const financieleVasteActiva =
            onderneming?.financieleVasteActiva
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'financieleVasteActiva')?.waardeOpening || 0;
        const onderhandenWerk =
            onderneming?.vlottendeActivaVoorraad?.filter((item) => item !== null).find((item) => item.omschrijving === 'onderhandenWerk')
                ?.waardeOpening || 0;
        const voorraad =
            onderneming?.vlottendeActivaVoorraad?.filter((item) => item !== null).find((item) => item.omschrijving === 'voorraad')
                ?.waardeOpening || 0;
        const debiteuren =
            onderneming?.vlottendeActivaVorderingen?.filter((item) => item !== null).find((item) => item.omschrijving === 'debiteuren')
                ?.waardeOpening || 0;
        const omzetbelastingVordering =
            onderneming?.vlottendeActivaVorderingen
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'omzetbelastingVordering')?.waardeOpening || 0;
        const overigeVorderingen =
            onderneming?.vlottendeActivaVorderingen
                ?.filter((item) => item !== null)
                .find((item) => item.omschrijving === 'overigeVorderingen')?.waardeOpening || 0;
        const effecten =
            onderneming?.effecten?.filter((item) => item !== null).find((item) => item.omschrijving === 'effecten')?.waardeOpening || 0;
        const liquideMiddelen =
            onderneming?.liquideMiddelen?.filter((item) => item !== null).find((item) => item.omschrijving === 'liquideMiddelen')
                ?.waardeOpening || 0;

        totaalActiva =
            goodWill +
            immaterieleVasteActiva +
            bedrijfsGebouwen +
            machinesEnInstallaties +
            andereVasteActiva +
            financieleVasteActiva +
            voorraad +
            onderhandenWerk +
            debiteuren +
            omzetbelastingVordering +
            overigeVorderingen +
            effecten +
            liquideMiddelen;

        const egalisatieReserve =
            onderneming?.eigenVermogen?.filter((item) => item !== null).find((item) => item.omschrijving === 'egalisatieReserve')
                ?.waardeOpening || 0;
        const oudedagsreserve =
            onderneming?.eigenVermogen?.filter((item) => item !== null).find((item) => item.omschrijving === 'oudedagsReserve')
                ?.waardeOpening || 0;
        const herinvesteringsreserve =
            onderneming?.eigenVermogen?.filter((item) => item !== null).find((item) => item.omschrijving === 'herinvesteringsReserve')
                ?.waardeOpening || 0;
        const garantieVoorziening =
            onderneming?.voorzieningen?.filter((item) => item !== null).find((item) => item.omschrijving === 'garantieVoorziening')
                ?.waardeOpening || 0;
        const overigeVoorzieningen =
            onderneming?.voorzieningen?.filter((item) => item !== null).find((item) => item.omschrijving === 'overigeVoorzieningen')
                ?.waardeOpening || 0;
        const kredietinstellingen =
            onderneming?.langlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'kredietinstellingen')
                ?.waardeOpening || 0;
        const langlopendeSchulden =
            onderneming?.langlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'langlopendeSchulden')
                ?.waardeOpening || 0;
        const crediteuren =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'crediteuren')
                ?.waardeOpening || 0;
        const omzetbelastingSchuld =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'omzetbelastingSchuld')
                ?.waardeOpening || 0;
        const loonheffingSchuld =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'loonheffingSchuld')
                ?.waardeOpening || 0;
        const kortlopendeSchulden =
            onderneming?.kortlopendeSchulden?.filter((item) => item !== null).find((item) => item.omschrijving === 'kortlopendeSchulden')
                ?.waardeOpening || 0;

        totaalPassivaZonderEv =
            egalisatieReserve +
            oudedagsreserve +
            herinvesteringsreserve +
            garantieVoorziening +
            overigeVoorzieningen +
            kredietinstellingen +
            langlopendeSchulden +
            crediteuren +
            loonheffingSchuld +
            omzetbelastingSchuld +
            kortlopendeSchulden;
        return totaalActiva - totaalPassivaZonderEv;
    }
}
